import { Page, Planogram } from "../../components";
import { MerchflowHeader } from "../../components/merchflows/MerchflowHeader/MerchflowHeader";
import { omniAtom } from "../../utils/atoms";
import { pages } from "../../utils";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { useModifiedPog } from "../../hooks/useModifiedPogAtom";
import { useApi } from "../../api";
import { useEffect, useRef, useState } from "react";
import { useLoaders } from "../../components/InlineLoader";
import { RoutePostCategories_categoryId_Stores_storeId_FixturesPog } from "@CommonApi/categories/_categoryId_/stores/_storeId_/fixturesPog";
import { sleep } from "../../utils/sleep";
import { PlanogramToolbar } from "../../components/Planogram/components/Toolbar/PlanogramToolbar";
import { AisleBreakEditingContext } from "../../components/Planogram/store/aisleBreakEditing";
import {
  AisleBreakProvenanceContext,
  readAisleBreakProvenance,
  type AisleBreakProvenanceByBay,
} from "../../components/Planogram/store/aisleBreakProvenance";
import { Button, Color, Flex, Spinner, Text } from "../../elements";
import { PlanogramHovererDetails } from "../../components/PlanogramExtensions/PlanogramHovererDetails/PlanogramHovererDetails";
import { PlanogramMetrics } from "../../components/PlanogramExtensions/PlanogramMetrics";
import { useParams } from "src/utils/useParams";
import { RoutePostFixtureGuessing } from "@CommonApi/fixtures-guessing";
import { useNavigation } from "src/utils/navigation";
import { useModalImagineShelvesSettings } from "src/modals/ImagineShelvesSettings/store/actions";
import {
  aisleBreakSuggestionsQueryKey,
  useAisleBreakSuggestionsQuery,
  useIsDeclaringAisleBreak,
} from "src/api/aisleBreaks";
import { queryClient } from "src/utils/queryClient";
import { AisleSplitSuggestions } from "src/pages/PsaRenderer/components/AisleSplitSuggestions";
import { suggestionsNotBrokenOn, withAisleBreak } from "src/utils/aisleBreaks";
import { useTranslation } from "src/i18n";

const pogAtom = omniAtom({
  prefix: pages.imagineShelvesFixturesGuessing_fixturesGuessingId_,
  key: "pog",
  value: null as null | Pog,
});

export const PageImagineShelvesFixturesGuessing = () => {
  const { fixturesGuessingId } = useParams<{
    fixturesGuessingId: number;
  }>();
  const { pog, setPog, revertPog, isModified, savePogAsOriginal, patchPog } =
    useModifiedPog(pogAtom);
  const { getFixturesGuessingPogApi, postCategories_categoryId_Stores_storeId_FixturesPogApi } =
    useApi();
  const { hasLoader } = useLoaders();
  const { t } = useTranslation("common");
  const { navigate } = useNavigation();
  const { openModalImagineShelvesSettings, imagineShelvesSettingsData } =
    useModalImagineShelvesSettings();

  const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
  const isSaveLoading = hasLoader(RoutePostCategories_categoryId_Stores_storeId_FixturesPog);
  const [categoryId, setCategoryId] = useState<null | number>(null);
  const [storeId, setStoreId] = useState<null | number>(null);
  // A guess has no stored breaks; once saved, the store's fixtures say who set each one.
  const [aisleBreakProvenance, setAisleBreakProvenance] = useState<AisleBreakProvenanceByBay>(
    () => new Map(),
  );
  // The pog on screen when a save comes back, to tell whether it was edited meanwhile.
  const latestPogRef = useRef(pog);
  useEffect(() => {
    latestPogRef.current = pog;
  }, [pog]);
  const aisleBreakTarget = categoryId !== null && storeId !== null ? { categoryId, storeId } : null;
  const suggestionsQuery = useAisleBreakSuggestionsQuery(aisleBreakTarget);
  const storeSuggestions = suggestionsQuery.data;
  // Save posts the pog on screen; until a declared break lands on it, that pog would undo it.
  const isDeclaringAisleBreak = useIsDeclaringAisleBreak();
  // Numbered against the store's fixtures, so they only fit a guess with the same bays.
  const aisleBreakSuggestions =
    storeSuggestions && pog && storeSuggestions.bayCount === pog.planogram.bays.length
      ? suggestionsNotBrokenOn(storeSuggestions.suggestions, pog)
      : [];

  const loadFixturesGuessingPog = async (fixturesGuessingId: number) => {
    const response = await getFixturesGuessingPogApi({
      fixturesGuessingId: String(fixturesGuessingId),
    });
    if (response) {
      setPog(response.fixturesGuessingPog);
      setCategoryId(response.categoryId);
      setStoreId(response.storeId);
    }
  };

  const onSave = async () => {
    if (!pog) return;
    const savedPog = pog;

    const response = await postCategories_categoryId_Stores_storeId_FixturesPogApi(
      { categoryId: String(categoryId), storeId: String(storeId) },
      savedPog.toNonCircular(),
    );

    if (response !== null) {
      if (categoryId !== null && storeId !== null) {
        void queryClient.invalidateQueries({
          queryKey: aisleBreakSuggestionsQueryKey(categoryId, storeId),
        });
      }
      setIsSaveSuccessful(true);
      await sleep(1000);
      // Adopt the saved fixtures: their bay ids name the stored sections, so a second save
      // keeps the writer of every aisle break it only kept. An edit made while saving stays
      // on screen as unsaved instead.
      if (response?.planogram && latestPogRef.current === savedPog) {
        setPog(response);
        setAisleBreakProvenance(readAisleBreakProvenance(response));
      } else {
        savePogAsOriginal(savedPog.toNonCircular());
      }
      setIsSaveSuccessful(false);
    }
  };

  const onClickImagine = async () => {
    if (categoryId !== null && storeId !== null) {
      openModalImagineShelvesSettings({
        categoryId: categoryId,
        storeIds: [storeId],
      });
    }
  };

  useEffect(() => {
    loadFixturesGuessingPog(fixturesGuessingId);
  }, [fixturesGuessingId]);

  useEffect(() => {
    return () => {
      setPog(null);
    };
  }, []);

  useEffect(() => {
    if (imagineShelvesSettingsData?.started === true && categoryId !== null) {
      setTimeout(() => {
        navigate(pages.imagineShelvesCategories_categoryId_, { categoryId });
      }, 2000);
    }
  }, [imagineShelvesSettingsData]);

  return (
    <Page>
      <MerchflowHeader
        imagine="SHELVES"
        categoryId={categoryId || undefined}
        storeId={storeId || undefined}
      />

      <Flex width="100%" align="center" gap="5px">
        <PlanogramToolbar pogAtom={pogAtom} showProducts={false} />

        <Button
          color="greenSmoke"
          onClick={onClickImagine}
          isEnabled={categoryId !== null && storeId !== null}
          isLoading={hasLoader(`POST_${RoutePostFixtureGuessing}`)}
          isSuccessful={imagineShelvesSettingsData?.started}
        >
          Imagine with AI
        </Button>
      </Flex>

      {aisleBreakTarget && suggestionsQuery.isLoading && (
        <Flex align="center" gap="8px">
          <Spinner />
          <Text variant="small2" color={Color.textSecondary}>
            {t("aisleBreaks.suggestion.loading")}
          </Text>
        </Flex>
      )}

      {aisleBreakTarget && suggestionsQuery.isError && (
        <Flex align="center" gap="8px">
          <Text variant="small2" color={Color.red}>
            {t("aisleBreaks.suggestion.loadFailed")}
          </Text>
          <Button size="small" variant="inverted" onClick={() => void suggestionsQuery.refetch()}>
            {t("aisleBreaks.suggestion.retry")}
          </Button>
        </Flex>
      )}

      {aisleBreakTarget && (
        <AisleSplitSuggestions
          key={`${aisleBreakTarget.categoryId}-${aisleBreakTarget.storeId}`}
          suggestions={aisleBreakSuggestions}
          target={aisleBreakTarget}
          // The guess is unsaved fixtures: its Save overwrites the store's, so it and the copy
          // Revert restores must both carry the break the store now holds.
          onDeclared={(afterBayNo) => patchPog(withAisleBreak(afterBayNo))}
          // A save in flight posts the pog without the break; landing after it would drop it.
          isDisabled={isSaveLoading}
        />
      )}

      <Flex minHeight="0" flexGrow column>
        <AisleBreakEditingContext.Provider value>
          <AisleBreakProvenanceContext.Provider value={aisleBreakProvenance}>
            <Planogram
              pogAtom={pogAtom}
              isEditable
              isPanelVisible
              isPog
              isWithBorder
              showPanelProducts={false}
              allowKonvaEditor
            />
          </AisleBreakProvenanceContext.Provider>
        </AisleBreakEditingContext.Provider>
      </Flex>

      <PlanogramHovererDetails />

      <Flex justify="between" align="center">
        <PlanogramMetrics pogAtom={pogAtom} />

        <Flex gap="10px">
          {isModified && (
            <Button
              color="red"
              variant="inverted"
              isDisabled={isSaveLoading || isSaveSuccessful}
              onClick={revertPog}
            >
              Revert Changes
            </Button>
          )}

          <Button
            isLoading={isSaveLoading}
            isSuccessful={isSaveSuccessful}
            isDisabled={isDeclaringAisleBreak}
            onClick={onSave}
          >
            Save
          </Button>
        </Flex>
      </Flex>
    </Page>
  );
};
